import React from 'react';
import { Layout } from '../../components/Layout';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Container } from '../../components/ui/Container';
import { Section } from '../../components/ui/Section';
import { Shield, GitMerge, CheckCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
export function StandardsMapping() {
  return <Layout data-id="element-1252">
      <Section spacing="lg" data-id="element-1253">
        <Container size="lg" data-id="element-1254">
          {/* Hero - Mobile Optimized */}
          <div className="mb-8 sm:mb-12 text-center animate-fade-in-up px-4" data-id="element-1255">
            <Badge variant="primary" size="lg" className="mb-4 sm:mb-6" data-id="element-1256">
              Standards Alignment
            </Badge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-100 mb-4 sm:mb-6" data-id="element-1257">
              Standards Mapping
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-400 max-w-3xl mx-auto" data-id="element-1258">
              How CyberSecurityProtocol aligns with global cybersecurity
              frameworks and standards.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8" data-id="element-1259">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8 sm:space-y-12" data-id="element-1260">
              {/* Core Standards */}
              <section className="animate-fade-in-up animation-delay-100" data-id="element-1261">
                <h2 className="text-xl sm:text-2xl font-bold text-slate-100 mb-4 sm:mb-6 font-mono flex items-center px-4 sm:px-0" data-id="element-1262">
                  <Shield className="h-5 w-5 sm:h-6 sm:w-6 mr-2 sm:mr-3 text-cyan-400 flex-shrink-0" data-id="element-1263" />
                  <span className="break-words" data-id="element-1264">Core Standards Alignment</span>
                </h2>
                <div className="space-y-4 sm:space-y-6" data-id="element-1265">
                  <StandardCard name="NIST Cybersecurity Framework (CSF) 2.0" description="Primary framework for organizational cybersecurity. Our Organization Protocol directly maps to the six core functions: Govern, Identify, Protect, Detect, Respond, and Recover." coverage="100%" color="cyan" mappings={['Govern → Organization/Govern', 'Identify → Organization/Identify', 'Protect → Organization/Protect', 'Detect → Organization/Detect', 'Respond → Organization/Respond', 'Recover → Organization/Recover']} data-id="element-1266" />

                  <StandardCard name="ISO/IEC 27001:2022" description="International standard for information security management systems (ISMS). Our protocols align with Annex A controls and ISMS requirements." coverage="95%" color="emerald" mappings={['A.5 Organizational Controls → Governance', 'A.6 People Controls → Identity & Access', 'A.7 Physical Controls → Critical Infrastructure', 'A.8 Technological Controls → Protect & Detect']} data-id="element-1267" />

                  <StandardCard name="CIS Controls v8" description="Prioritized set of actions to protect organizations from cyber threats. Our protocols incorporate all 18 CIS Controls with implementation guidance." coverage="100%" color="amber" mappings={['Basic Controls (1-6) → Personal & Organization', 'Foundational Controls (7-16) → Organization Protocol', 'Organizational Controls (17-18) → Governance']} data-id="element-1268" />
                </div>
              </section>

              {/* Framework Comparison - Mobile Optimized */}
              <section className="animate-fade-in-up animation-delay-200" data-id="element-1269">
                <h2 className="text-xl sm:text-2xl font-bold text-slate-100 mb-4 sm:mb-6 font-mono flex items-center px-4 sm:px-0" data-id="element-1270">
                  <GitMerge className="h-5 w-5 sm:h-6 sm:w-6 mr-2 sm:mr-3 text-cyan-400 flex-shrink-0" data-id="element-1271" />
                  <span className="break-words" data-id="element-1272">
                    Framework Comparison Matrix
                  </span>
                </h2>
                <Card glass padding="lg" data-id="element-1273">
                  <div className="overflow-x-auto -mx-4 sm:mx-0" data-id="element-1274">
                    <div className="min-w-[600px] px-4 sm:px-0" data-id="element-1275">
                      <table className="w-full text-xs sm:text-sm" data-id="element-1276">
                        <thead data-id="element-1277">
                          <tr className="border-b border-slate-800" data-id="element-1278">
                            <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-semibold text-slate-200" data-id="element-1279">
                              Domain
                            </th>
                            <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-semibold text-slate-200" data-id="element-1280">
                              NIST CSF 2.0
                            </th>
                            <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-semibold text-slate-200" data-id="element-1281">
                              ISO 27001
                            </th>
                            <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-semibold text-slate-200" data-id="element-1282">
                              CIS Controls
                            </th>
                          </tr>
                        </thead>
                        <tbody className="text-slate-400" data-id="element-1283">
                          <tr className="border-b border-slate-800/50" data-id="element-1284">
                            <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium text-slate-300" data-id="element-1285">
                              Governance
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4" data-id="element-1286">
                              GV (Govern)
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4" data-id="element-1287">
                              A.5 Organizational
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4" data-id="element-1288">
                              Control 17-18
                            </td>
                          </tr>
                          <tr className="border-b border-slate-800/50" data-id="element-1289">
                            <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium text-slate-300" data-id="element-1290">
                              Asset Management
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4" data-id="element-1291">
                              ID.AM (Identify)
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4" data-id="element-1292">
                              A.5.9 Inventory
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4" data-id="element-1293">
                              Control 1-2
                            </td>
                          </tr>
                          <tr className="border-b border-slate-800/50" data-id="element-1294">
                            <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium text-slate-300" data-id="element-1295">
                              Access Control
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4" data-id="element-1296">
                              PR.AC (Protect)
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4" data-id="element-1297">
                              A.5.15-18 Access
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4" data-id="element-1298">
                              Control 5-6
                            </td>
                          </tr>
                          <tr className="border-b border-slate-800/50" data-id="element-1299">
                            <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium text-slate-300" data-id="element-1300">
                              Data Protection
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4" data-id="element-1301">
                              PR.DS (Protect)
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4" data-id="element-1302">
                              A.5.33-34 Crypto
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4" data-id="element-1303">
                              Control 3, 11
                            </td>
                          </tr>
                          <tr className="border-b border-slate-800/50" data-id="element-1304">
                            <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium text-slate-300" data-id="element-1305">
                              Threat Detection
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4" data-id="element-1306">
                              DE (Detect)
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4" data-id="element-1307">
                              A.8.15-16 Logging
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4" data-id="element-1308">
                              Control 8, 13
                            </td>
                          </tr>
                          <tr className="border-b border-slate-800/50" data-id="element-1309">
                            <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium text-slate-300" data-id="element-1310">
                              Incident Response
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4" data-id="element-1311">
                              RS (Respond)
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4" data-id="element-1312">
                              A.5.24-28 Incident
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4" data-id="element-1313">
                              Control 17
                            </td>
                          </tr>
                          <tr data-id="element-1314">
                            <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium text-slate-300" data-id="element-1315">
                              Business Continuity
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4" data-id="element-1316">
                              RC (Recover)
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4" data-id="element-1317">
                              A.5.29-30 Continuity
                            </td>
                            <td className="py-2 sm:py-3 px-2 sm:px-4" data-id="element-1318">
                              Control 11
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-4 px-4 sm:px-0" data-id="element-1319">
                    Scroll horizontally to view full table on mobile
                  </p>
                </Card>
              </section>

              {/* Additional Standards - Mobile Optimized */}
              <section className="animate-fade-in-up animation-delay-300" data-id="element-1320">
                <h2 className="text-xl sm:text-2xl font-bold text-slate-100 mb-4 sm:mb-6 font-mono px-4 sm:px-0" data-id="element-1321">
                  Additional Standards & Guidelines
                </h2>
                <Card glass padding="lg" data-id="element-1322">
                  <p className="text-xs sm:text-sm text-slate-400 mb-4 sm:mb-6" data-id="element-1323">
                    Additional standards and guidelines (e.g., sectoral, risk,
                    privacy, or cloud-specific) are referenced where
                    contextually relevant:
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" data-id="element-1324">
                    <AdditionalStandard name="GDPR (EU)" context="Privacy & Data Protection" link="/compliance/gdpr" data-id="element-1325" />
                    <AdditionalStandard name="PDP Indonesia" context="National Data Protection" link="/compliance/indonesia" data-id="element-1326" />
                    <AdditionalStandard name="PDPA Singapore" context="Regional Compliance" link="/compliance/singapore" data-id="element-1327" />
                    <AdditionalStandard name="LGPD Brazil" context="Latin America Privacy" link="/compliance/brazil" data-id="element-1328" />
                    <AdditionalStandard name="NIST SP 800-53" context="Security Controls Catalog" link="/government" data-id="element-1329" />
                    <AdditionalStandard name="ISO 27017/27018" context="Cloud Security" link="/organization" data-id="element-1330" />
                    <AdditionalStandard name="PCI DSS" context="Payment Card Security" link="/compliance/global" data-id="element-1331" />
                    <AdditionalStandard name="HIPAA" context="Healthcare Security" link="/compliance/global" data-id="element-1332" />
                  </div>
                </Card>
              </section>

              {/* Implementation Notes - Mobile Optimized */}
              <section className="animate-fade-in-up animation-delay-400" data-id="element-1333">
                <Card glass padding="lg" className="border-cyan-500/20" data-id="element-1334">
                  <div className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-4" data-id="element-1335">
                    <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl bg-cyan-500/10 flex items-center justify-center text-cyan-400 flex-shrink-0" data-id="element-1336">
                      <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6" data-id="element-1337" />
                    </div>
                    <div data-id="element-1338">
                      <h3 className="text-base sm:text-lg font-bold text-slate-100 mb-2" data-id="element-1339">
                        Implementation Approach
                      </h3>
                      <p className="text-xs sm:text-sm text-slate-400 mb-3 sm:mb-4" data-id="element-1340">
                        Our protocols are designed to be framework-agnostic
                        while maintaining strong alignment with global
                        standards. This allows organizations to:
                      </p>
                      <ul className="space-y-2 text-xs sm:text-sm text-slate-400" data-id="element-1341">
                        <li className="flex items-start" data-id="element-1342">
                          <span className="text-cyan-400 mr-2 flex-shrink-0" data-id="element-1343">
                            ▸
                          </span>
                          <span data-id="element-1344">
                            Adopt guidance without being locked into a single
                            framework
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-1345">
                          <span className="text-cyan-400 mr-2 flex-shrink-0" data-id="element-1346">
                            ▸
                          </span>
                          <span data-id="element-1347">
                            Map controls to multiple standards simultaneously
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-1348">
                          <span className="text-cyan-400 mr-2 flex-shrink-0" data-id="element-1349">
                            ▸
                          </span>
                          <span data-id="element-1350">
                            Demonstrate compliance across different regulatory
                            requirements
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-1351">
                          <span className="text-cyan-400 mr-2 flex-shrink-0" data-id="element-1352">
                            ▸
                          </span>
                          <span data-id="element-1353">
                            Adapt to evolving standards and emerging threats
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </Card>
              </section>
            </div>

            {/* Sidebar - Mobile: Below content, Desktop: Sticky */}
            <div className="space-y-4 sm:space-y-6" data-id="element-1354">
              <Card glass padding="lg" className="lg:sticky lg:top-24 animate-fade-in-up animation-delay-500" data-id="element-1355">
                <h3 className="text-base sm:text-lg font-bold text-slate-100 mb-3 sm:mb-4 font-mono" data-id="element-1356">
                  Quick Links
                </h3>
                <div className="space-y-1 sm:space-y-2" data-id="element-1357">
                  <Link to="/about/mission" className="block p-2 rounded text-sm hover:bg-slate-800/50 text-slate-400 hover:text-cyan-400 transition-colors" data-id="element-1358">
                    Mission & Vision
                  </Link>
                  <Link to="/about/scope" className="block p-2 rounded text-sm hover:bg-slate-800/50 text-slate-400 hover:text-cyan-400 transition-colors" data-id="element-1359">
                    Scope & Non-Guarantee
                  </Link>
                  <Link to="/about/standards" className="block p-2 rounded text-sm bg-cyan-500/10 text-cyan-400 font-medium border border-cyan-500/20" data-id="element-1360">
                    Standards Mapping
                  </Link>
                  <Link to="/about/changelog" className="block p-2 rounded text-sm hover:bg-slate-800/50 text-slate-400 hover:text-cyan-400 transition-colors" data-id="element-1361">
                    Changelog
                  </Link>
                  <Link to="/about/governance" className="block p-2 rounded text-sm hover:bg-slate-800/50 text-slate-400 hover:text-cyan-400 transition-colors" data-id="element-1362">
                    Governance
                  </Link>
                  <Link to="/about/disclaimer" className="block p-2 rounded text-sm hover:bg-slate-800/50 text-slate-400 hover:text-cyan-400 transition-colors" data-id="element-1363">
                    Legal Disclaimer
                  </Link>
                  <Link to="/about/contact" className="block p-2 rounded text-sm hover:bg-slate-800/50 text-slate-400 hover:text-cyan-400 transition-colors" data-id="element-1364">
                    Contact Us
                  </Link>
                </div>
              </Card>

              {/* Coverage Stats */}
              <Card glass padding="lg" className="bg-emerald-500/5 border-emerald-500/20" data-id="element-1365">
                <h4 className="text-xs sm:text-sm font-bold text-slate-200 uppercase mb-3 sm:mb-4" data-id="element-1366">
                  Coverage Stats
                </h4>
                <div className="space-y-3" data-id="element-1367">
                  <div data-id="element-1368">
                    <div className="flex justify-between text-xs mb-1" data-id="element-1369">
                      <span className="text-slate-400" data-id="element-1370">NIST CSF 2.0</span>
                      <span className="text-emerald-400 font-semibold" data-id="element-1371">
                        100%
                      </span>
                    </div>
                    <div className="h-2 bg-slate-800 rounded-full overflow-hidden" data-id="element-1372">
                      <div className="h-full bg-emerald-500 rounded-full" style={{
                      width: '100%'
                    }} data-id="element-1373"></div>
                    </div>
                  </div>
                  <div data-id="element-1374">
                    <div className="flex justify-between text-xs mb-1" data-id="element-1375">
                      <span className="text-slate-400" data-id="element-1376">CIS Controls v8</span>
                      <span className="text-emerald-400 font-semibold" data-id="element-1377">
                        100%
                      </span>
                    </div>
                    <div className="h-2 bg-slate-800 rounded-full overflow-hidden" data-id="element-1378">
                      <div className="h-full bg-emerald-500 rounded-full" style={{
                      width: '100%'
                    }} data-id="element-1379"></div>
                    </div>
                  </div>
                  <div data-id="element-1380">
                    <div className="flex justify-between text-xs mb-1" data-id="element-1381">
                      <span className="text-slate-400" data-id="element-1382">ISO 27001:2022</span>
                      <span className="text-cyan-400 font-semibold" data-id="element-1383">95%</span>
                    </div>
                    <div className="h-2 bg-slate-800 rounded-full overflow-hidden" data-id="element-1384">
                      <div className="h-full bg-cyan-500 rounded-full" style={{
                      width: '95%'
                    }} data-id="element-1385"></div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </Container>
      </Section>
    </Layout>;
}
interface StandardCardProps {
  name: string;
  description: string;
  coverage: string;
  color: 'cyan' | 'emerald' | 'amber';
  mappings: string[];
}
function StandardCard({
  name,
  description,
  coverage,
  color,
  mappings
}: StandardCardProps) {
  const colors = {
    cyan: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20',
    emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    amber: 'text-amber-400 bg-amber-500/10 border-amber-500/20'
  };
  return <Card glass padding="lg" className={`border ${colors[color].split(' ')[2]}`} data-id="element-1386">
      <div className="flex flex-col sm:flex-row items-start justify-between mb-3 sm:mb-4 gap-2" data-id="element-1387">
        <h3 className="text-base sm:text-lg font-bold text-slate-100 break-words flex-1" data-id="element-1388">
          {name}
        </h3>
        <Badge variant={color === 'cyan' ? 'primary' : color === 'emerald' ? 'success' : 'warning'} size="sm" className="flex-shrink-0" data-id="element-1389">
          {coverage}
        </Badge>
      </div>
      <p className="text-xs sm:text-sm text-slate-400 mb-3 sm:mb-4" data-id="element-1390">
        {description}
      </p>
      <div data-id="element-1391">
        <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2 sm:mb-3" data-id="element-1392">
          Key Mappings
        </h4>
        <ul className="space-y-1.5 sm:space-y-2" data-id="element-1393">
          {mappings.map((mapping, index) => <li key={index} className="flex items-start text-xs sm:text-sm text-slate-300" data-id="element-1394">
              <span className={`${colors[color].split(' ')[0]} mr-2 mt-0.5 flex-shrink-0`} data-id="element-1395">
                ▸
              </span>
              <span className="break-words" data-id="element-1396">{mapping}</span>
            </li>)}
        </ul>
      </div>
    </Card>;
}
function AdditionalStandard({
  name,
  context,
  link
}: {
  name: string;
  context: string;
  link: string;
}) {
  return <Link to={link} className="block p-3 rounded-lg bg-slate-900/50 border border-slate-800 hover:border-cyan-500/30 transition-all group touch-manipulation" data-id="element-1397">
      <div className="flex items-center justify-between mb-1 gap-2" data-id="element-1398">
        <h4 className="text-xs sm:text-sm font-semibold text-slate-200 group-hover:text-cyan-400 transition-colors break-words flex-1" data-id="element-1399">
          {name}
        </h4>
        <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4 text-slate-600 group-hover:text-cyan-400 transition-colors flex-shrink-0" data-id="element-1400" />
      </div>
      <p className="text-xs text-slate-500" data-id="element-1401">{context}</p>
    </Link>;
}